import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSwiper } from 'swiper/react';

export const DotButton = ({ index, total }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiper = useSwiper();
  const { t } = useTranslation();

  useEffect(() => {
    swiper.on('slideChange', (swiper) => {
      setActiveIndex(swiper.activeIndex);
    });
    return () => swiper.off('slideChange');
  }, [swiper]);

  return (
    <ImageSliderDotButton
      isSelected={activeIndex === index}
      type="button"
      onClick={() => swiper.slideTo(index)}
      aria-label={t('aria_labels.carousel_page', {
        index: index + 1,
        total,
      })}
      aria-current={activeIndex === index}
    />
  );
};

export const PrevButton = () => {
  const swiper = useSwiper();
  const { t } = useTranslation();

  const [isBeginning, setIsBeginning] = useState(true);
  useEffect(() => {
    swiper.on('slideChange', (swiper) => {
      setIsBeginning(swiper.isBeginning);
    });
    return () => swiper.off('slideChange');
  }, [swiper]);

  return (
    <ImageSliderButton
      next={false}
      onClick={() => swiper.slidePrev()}
      aria-label={t('aria_labels.previous_image')}
      aria-disabled={isBeginning}
    >
      <svg
        width="40"
        height="41"
        viewBox="0 0 40 41"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g filter="url(#filter0_d_5_153)">
          <circle cx="20" cy="18.5" r="16" fill="white" />
        </g>
        <rect
          width="1.5"
          height="10"
          transform="matrix(-4.37114e-08 1 1 4.37114e-08 16 17.25)"
          fill="#212B36"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.8973 23.5L20 22.4969L15.9959 18L20 13.5031L18.8973 12.5L14 18L18.8973 23.5Z"
          fill="#212B36"
        />
        <defs>
          <filter
            id="filter0_d_5_153"
            x="0"
            y="0.5"
            width="40"
            height="40"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.760784 0 0 0 0 0.760784 0 0 0 0 0.760784 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_5_153"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_5_153"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    </ImageSliderButton>
  );
};

export const NextButton = () => {
  const swiper = useSwiper();
  const { t } = useTranslation();
  const [isEnd, setIsEnd] = useState(true);
  useEffect(() => {
    swiper.on('slideChange', (swiper) => {
      setIsEnd(swiper.isEnd);
    });
    return () => swiper.off('slideChange');
  }, [swiper]);

  return (
    <ImageSliderButton
      next
      onClick={() => swiper.slideNext()}
      aria-label={t('aria_labels.next_image')}
      aria-disabled={isEnd}
    >
      <svg
        width="40"
        height="41"
        viewBox="0 0 40 41"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g filter="url(#filter0_d_5_153)">
          <circle cx="20" cy="18.5" r="16" fill="white" />
        </g>
        <rect
          width="1.5"
          height="10"
          transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 24 18.75)"
          fill="#212B36"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M21.1027 12.5L20 13.5031L24.0041 18L20 22.4969L21.1027 23.5L26 18L21.1027 12.5Z"
          fill="#212B36"
        />
        <defs>
          <filter
            id="filter0_d_5_153"
            x="0"
            y="0.5"
            width="40"
            height="40"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.760784 0 0 0 0 0.760784 0 0 0 0 0.760784 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_5_153"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_5_153"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    </ImageSliderButton>
  );
};

const ImageSliderDotButton = styled.button.attrs<{ isSelected: boolean }>(
  ({ isSelected }) => ({
    className: `image-slider-dot-button ${
      isSelected ? 'image-slider-dot-button--is-active' : ''
    }`,
  }),
)<{ isSelected: boolean }>`
  background-color: #d1d9e0;
  cursor: pointer;
  position: relative;
  padding: 0;
  outline: 0;
  border: 0;
  width: 10px;
  height: 10px;
  margin-right: 7.5px;
  margin-left: 7.5px;
  display: flex;
  align-items: center;
  border-radius: 50%;
  ${({ isSelected }) =>
    isSelected
      ? `  background-color: #212b36;
  opacity: 1;`
      : ''};
`;

const ImageSliderButton = styled.button.attrs<{ next?: boolean }>(
  ({ next }) => ({
    className: `image-slider-button ${next ? 'next' : 'prev'}`,
  }),
)<{ next: boolean }>`
  outline: 0;
  cursor: pointer;
  background-color: transparent;
  touch-action: manipulation;
  position: absolute;
  z-index: 1;
  top: 45%;
  transform: translateY(-50%);
  border: 0;
  width: 30px;
  height: 30px;
  justify-content: center;
  align-items: center;
  fill: #1bcacd;
  padding: 0;
  &.swiper-button-disabled {
    display: none;
  }
  &.prev {
    left: -3px;
  }
  &.next {
    right: 6px;
  }
`;
